import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, User, Briefcase, FileText, Image as ImageIcon, Download } from "lucide-react";
import Button from "@/components/ui/Button";

const StepModal = ({ isOpen, onClose, step, currentLang }) => {
  const translations = {
    ar: {
      step1: "البيانات الشخصية",
      step1Desc: "في هذه الخطوة، ستقوم بملء بياناتك الشخصية الأساسية بدقة وعناية.",
      step1Details: [
        "الاسم الكامل بالعربية والإنجليزية",
        "نوع الهوية (الرقم القومي أو جواز السفر)",
        "الجنسية الأصلية",
        "رقم هاتف صحيح وصالح",
        "بريد إلكتروني نشط وموثوق",
      ],
      step2: "بيانات الاستثمار",
      step2Desc: "تحديد نوع القطاع والشركة المرغوب الاستثمار فيها.",
      step2Details: [
        "اختيار القطاع الاقتصادي المناسب",
        "تحديد الشركة المستهدفة",
        "حساب مبلغ الاستثمار بالمليون",
      ],
      step3: "المستندات المطلوبة",
      step3Desc: "ارفع جميع المستندات المطلوبة بصيغة واضحة وقابلة للقراءة.",
      step3Details: [
        "صورة تحقيق الشخصية (صيغة PDF أو صورة)",
        "السجل التجاري الحديث والموثق",
        "البطاقة الضريبية سارية المفعول",
      ],
      tips: "نصائح مهمة",
      docExamples: "نماذج المستندات المطلوبة",
      downloadSample: "تحميل نموذج",
      viewLarger: "عرض صورة أكبر",
    },
    en: {
      step1: "Personal Data",
      step1Desc: "In this step, you will fill in your basic personal information accurately.",
      step1Details: [
        "Full name in Arabic and English",
        "ID type (National ID or Passport)",
        "Original nationality",
        "Valid and correct phone number",
        "Active and reliable email address",
      ],
      step2: "Investment Data",
      step2Desc: "Identify the sector and company you wish to invest in.",
      step2Details: [
        "Choose the appropriate economic sector",
        "Identify the target company",
        "Calculate the investment amount in millions",
      ],
      step3: "Required Documents",
      step3Desc: "Upload all required documents in clear and readable format.",
      step3Details: [
        "Identity verification photo (PDF or image)",
        "Recent and certified commercial register",
        "Valid tax card",
      ],
      tips: "Important Tips",
      docExamples: "Required Document Samples",
      downloadSample: "Download Sample",
      viewLarger: "View Larger",
    },
  };

  const BASE = "/public/assets/images/investSectors/";
  
  // صور المستندات للخطوة الثالثة
  const documentImages = {
    idCard: "/assets/images/invest_paper/personal.webp",
    commercialRegister: "assets/images/invest_paper/commerce.webp",
    taxCard: "/assets/images/invest_paper/commerce2.webp",
  };

  const stepData = {
    1: {
      title: translations[currentLang]?.step1 || translations.ar.step1,
      description: translations[currentLang]?.step1Desc || translations.ar.step1Desc,
      details: translations[currentLang]?.step1Details || translations.ar.step1Details,
      icon: User,
      color: "from-blue-500 to-cyan-500",
      image: BASE + "5.webp",
    },
    2: {
      title: translations[currentLang]?.step2 || translations.ar.step2,
      description: translations[currentLang]?.step2Desc || translations.ar.step2Desc,
      details: translations[currentLang]?.step2Details || translations.ar.step2Details,
      icon: Briefcase,
      color: "from-green-500 to-emerald-500",
      image: BASE + "1.webp",
    },
    3: {
      title: translations[currentLang]?.step3 || translations.ar.step3,
      description: translations[currentLang]?.step3Desc || translations.ar.step3Desc,
      details: translations[currentLang]?.step3Details || translations.ar.step3Details,
      icon: FileText,
      color: "from-purple-500 to-pink-500",
      image: BASE + "2.webp",
      documentSamples: [
        {
          title: currentLang === "ar" ? "بطاقة الهوية / جواز السفر" : "ID Card / Passport",
          image: documentImages.idCard,
          description: currentLang === "ar" 
            ? "صورة واضحة للوجه الأمامي والخلفي" 
            : "Clear photo of front and back",
        },
        {
          title: currentLang === "ar" ? "السجل التجاري" : "Commercial Register",
          image: documentImages.commercialRegister,
          description: currentLang === "ar" 
            ? "نسخة حديثة ومصدقة" 
            : "Recent and certified copy",
        },
        {
          title: currentLang === "ar" ? "البطاقة الضريبية" : "Tax Card",
          image: documentImages.taxCard,
          description: currentLang === "ar" 
            ? "سارية المفعول وتظهر التفاصيل كاملة" 
            : "Valid and showing full details",
        },
      ],
    },
  };

  const data = stepData[step];
  if (!isOpen || !data) return null;
  const IconComponent = data.icon;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white dark:bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
        >
          <div className="relative h-64 overflow-hidden">
            <img 
              src={data.image} 
              alt={data.title} 
              className="w-full h-full object-cover" 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <button 
              onClick={onClose} 
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 flex items-center justify-center text-white transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="absolute bottom-6 left-6 right-6 z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${data.color} flex items-center justify-center text-white shadow-lg`}>
                  <IconComponent className="w-7 h-7" />
                </div>
                <div className={`px-3 py-1 bg-gradient-to-r ${data.color} rounded-full text-white text-sm font-semibold`}>
                  {currentLang === "ar" ? `الخطوة ${step} من 3` : `Step ${step} of 3`}
                </div>
              </div>
              <h2 className="text-4xl font-bold text-white drop-shadow-lg">{data.title}</h2>
            </div>
          </div>
          
          <div className="p-8 md:p-12">
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              {data.description}
            </p>
            
            {/* قسم المستندات للخطوة الثالثة فقط */}
            {step === 3 && data.documentSamples && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  {translations[currentLang]?.docExamples || translations.ar.docExamples}:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {data.documentSamples.map((doc, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={doc.image} 
                          alt={doc.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <button className="absolute top-2 right-2 bg-white/90 dark:bg-gray-900/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <ImageIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                        </button>
                      </div>
                      <div className="p-4">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">{doc.title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{doc.description}</p>
                        <div className="flex gap-2">
                          <button className="flex-1 flex items-center justify-center gap-2 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all text-sm">
                            <Download className="w-4 h-4" />
                            {translations[currentLang]?.downloadSample || translations.ar.downloadSample}
                          </button>
                          <button className="px-3 py-2 border border-purple-500 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-sm">
                            {translations[currentLang]?.viewLarger || translations.ar.viewLarger}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {translations[currentLang]?.tips || translations.ar.tips}:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.details.map((detail, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 hover:shadow-md transition-shadow"
                  >
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${data.color} flex items-center justify-center text-white flex-shrink-0 mt-0.5`}>
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{detail}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={onClose} 
                className={`flex-1 bg-gradient-to-r ${data.color} text-white font-semibold py-3 rounded-lg hover:shadow-lg transition-all`}
              >
                {currentLang === "ar" ? "فهمت - ابدأ الآن" : "Got it - Start Now"}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StepModal;